import type { ElementRef } from '@angular/core';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  input,
  NgZone,
  output,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';

import { ChatWorkflowStorageUpdatedService } from '@rpgc-ai/services/storage/chat-workflow-storage-updated.service';

import type { GameEvent, GameHandle, GameState, MountGameOptions } from '@rpgcai/game-runtime';

import { library as mockLibrary } from './lib.mock.json';
import { WorkflowTransportService } from './workflow-transport.service';

// TEMP — backend does not yet return the blueprint `library` (MR not merged).
// Feed the mock so screens referencing extension uiComponents render. Schema-provided
// `library` entries win on key collision, so this is safe to drop once the backend
// ships `library` in the wire schema.
const MOCK_LIBRARY = mockLibrary as unknown as MountGameOptions['library'];

@Component({
  selector: 'app-ui-game-runtime',
  templateUrl: './game-runtime.component.html',
  styleUrl: './game-runtime.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GameRuntimeComponent {
  readonly nodes = input.required<unknown[]>();
  readonly storage = input<Record<string, unknown>>({});
  readonly extensionId = input<string>();
  readonly includeLibrary = input<boolean>(false);
  readonly chatId = input<string>();
  readonly workflowId = input<string>();
  // Opt-in FE autosave (game-runtime build.465+). When enabled the package keeps
  // the backend DB current via /play/save-state so reconnect (freshStart:false) and
  // cross-session reopen rehydrate from current state, not stale storage. Default OFF.
  readonly autoSave = input<MountGameOptions['autoSave']>();
  // Opt-in FE keepalive (game-runtime build.467+). When enabled the package
  // proactively re-opens the stream before the backend's 10-min screen
  // close-waiter timeout, silently (no screen remount, no badge flip), keeping
  // an idle interactive screen alive. Default OFF.
  readonly keepAlive = input<MountGameOptions['keepAlive']>();
  // `$host` namespace data forwarded to the embedded game (read-only, FE-only
  // host→schema channel). Reactive: replacing the object re-applies via
  // GameHandle.setHostData, so e.g. a live token balance updates schema
  // expressions like `$host.tokenBalance`. Default absent → `$host.*` undefined.
  readonly hostData = input<Record<string, unknown>>();

  readonly stateChange = output<GameState>();
  readonly gameEvent = output<GameEvent>();
  readonly gameError = output<string>();
  readonly screenReady = output<string>();

  protected readonly screen = viewChild.required<ElementRef<HTMLElement>>('screen');
  protected readonly projected = viewChild.required<ElementRef<HTMLElement>>('projected');
  readonly #ngZone = inject(NgZone);
  readonly #transport = inject(WorkflowTransportService);
  readonly #bridgeService = inject(ChatWorkflowStorageUpdatedService);
  readonly #document = inject(DOCUMENT);

  #handle: GameHandle | null = null;
  #slotEl: HTMLElement | null = null;
  // Phase 129 CR-01 — buffer for bridge events arriving before handle is assigned
  // (pre-mount window). Flushed in afterNextRender after mountGame() completes.
  #pendingStateSync: { storageData: Record<string, unknown> } | null = null;

  constructor() {
    this.#addBridgeStateSyncEffect();
    this.#addHostDataEffect();

    afterNextRender(() => {
      void this.#ngZone.runOutsideAngular(async () => {
        // @rpgcai/game-runtime pulls screen-renderer/css-tree (CommonJS with a dynamic JSON
        // require) which breaks SSR route extraction. Load it lazily here — afterNextRender is
        // browser-only, so css-tree never enters the server module graph.
        const { mountGame } = await import('@rpgcai/game-runtime');

        // Create stable slot element and reparent ng-content children into it
        this.#slotEl = this.#document.createElement('div');
        const fragment = this.#document.createDocumentFragment();
        let child: ChildNode | null;
        while ((child = this.projected().nativeElement.firstChild)) {
          fragment.appendChild(child);
        }
        this.#slotEl.appendChild(fragment);

        this.#handle = mountGame({
          screenContainer: this.screen().nativeElement,
          transport: this.#transport,
          slotEl: this.#slotEl,
          // TEMP — opt-in mock library, enabled only by the Workshop view until the
          // backend ships `library` in the wire schema (MR not merged).
          library: this.includeLibrary() ? MOCK_LIBRARY : undefined,
          autoSave: this.autoSave(),
          keepAlive: this.keepAlive(),
        });

        this.#handle.on('state:change', (s, meta) => {
          // Phase 129 R-06 (Part B.5) — bridge-origin patches must not re-trigger the
          // chat-app save pipeline. chat.component.ts.onGameStateChange feeds the
          // throttled #stateChange$ → POST /runtime/play/save-state pipeline; skipping
          // the Angular output here starves it for bridge events. Symmetric with the
          // editor guard in GameShell.tsx.
          if (meta?.source === 'bridge') {return;}
          this.#ngZone.run(() => this.stateChange.emit(s));
        });
        this.#handle.on('game:event', (e) =>
          this.#ngZone.run(() => this.gameEvent.emit(e)));
        this.#handle.on('game:error', (e) =>
          this.#ngZone.run(() => this.gameError.emit(e)));
        this.#handle.on('screen:ready', (id) =>
          this.#ngZone.run(() => this.screenReady.emit(id)));

        this.#handle.start(this.nodes(), this.storage(), this.extensionId());

        // Seed the `$host` namespace with the current host data (e.g. user token
        // balance) right after mount. Subsequent changes flow through
        // #addHostDataEffect → setHostData (replace-only).
        const initialHostData = this.hostData();
        if (initialHostData) {
          this.#handle.setHostData(initialHostData);
        }

        // Phase 129 CR-01 — flush any bridge event buffered during the pre-mount window
        // (effect ran before handle was assigned, stored payload in #pendingStateSync).
        if (this.#pendingStateSync) {
          this.#handle.applyStateSync(this.#pendingStateSync);
          this.#pendingStateSync = null;
        }

        // Phase 129 R-05 — mirror chat.component.ts:296 idiom: consumer owns explicit
        // connect() on WS-subscriber services. Idempotent across remounts (providedIn: 'root').
        this.#bridgeService.connect();
      });
    });

    inject(DestroyRef).onDestroy(() => {
      if (this.#handle) {
        // Reverse reparent: return Angular content before destroy
        if (this.#slotEl) {
          let node: ChildNode | null;
          while ((node = this.#slotEl.firstChild)) {
            this.projected().nativeElement.appendChild(node);
          }
        }
        this.#handle.destroy();
        this.#handle = null;
        this.#slotEl = null;
      }
      // Phase 129 CR-02 — pair with connect() called in afterNextRender.
      // Prevents leaking the WS subscription across route changes (providedIn: 'root').
      this.#bridgeService.disconnect();
    });
  }

  respond(action: string, value?: string): Promise<void> {
    return this.#handle?.respond(action, value) ?? Promise.resolve();
  }

  reset(): void {
    this.#handle?.reset();
  }

  /**
   * Phase 129 R-05 (D-07/D-08/D-09) — wires `ChatWorkflowStorageUpdatedService` → `applyStateSync`
   * with defensive double-filter on chatId AND workflowId. effect() auto-cleans on
   * component destruction (Angular DI tree teardown); no manual unsubscribe.
   */
  #addBridgeStateSyncEffect(): void {
    effect(() => {
      const event = this.#bridgeService.message();
      if (!event) {
        return;
      }

      const data = event.data;
      const currentChatId = untracked(this.chatId);
      const currentWorkflowId = untracked(this.workflowId);

      if (!currentChatId || data.chatId !== currentChatId) {
        return;
      }
      if (!currentWorkflowId || data.workflowId !== currentWorkflowId) {
        return;
      }

      // Phase 129 WR-02 — runtime guard at the trust boundary: network payload types
      // are claims, not guarantees. Drop malformed storageData to prevent null/array/
      // primitive from propagating into applyStateSync and breaking downstream consumers.
      if (
        !data.storageData ||
        typeof data.storageData !== 'object' ||
        Array.isArray(data.storageData)
      ) {
        return;
      }

      untracked(() => {
        if (this.#handle) {
          this.#handle.applyStateSync({ storageData: data.storageData });
        } else {
          // Phase 129 CR-01 — handle not yet assigned (pre-mount window).
          // Buffer the latest event; afterNextRender will flush it after mountGame().
          this.#pendingStateSync = { storageData: data.storageData };
        }
      });
    });
  }

  /**
   * Forwards the reactive `hostData` input to the embedded game's `$host`
   * namespace. Fires on every `hostData()` change (e.g. async-loaded token
   * balance). No-op before the handle mounts — the initial value is seeded in
   * afterNextRender right after `start()`. `setHostData` is replace-only and
   * reads no signals, so the write runs untracked.
   */
  #addHostDataEffect(): void {
    effect(() => {
      const hd = this.hostData();
      if (!hd) {
        return;
      }
      untracked(() => this.#handle?.setHostData(hd));
    });
  }
}
